import clsx from 'clsx';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Form, Overlay, Popover } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../../../../../component/input-component/style.scss';
import { COLUMNS } from '../../Column';
import TableSelect from './TableSelect';
import { LOAI_VAT_TU } from '../../../../../phan-he-kham-benh/constants/VatTu';
import { searchVatTu } from '../../../../../phan-he-kham-benh/services/VatTuService';
import { RESPONSE_MESSAGE } from '../../../../../utils/Constant';
import { useDebounce } from '../../../../../../../_metronic/helpers';

type Props = {
	maKhoMau: string;
	maLoaiMau: string;
	setMauSelected: (value: any) => void;
	label?: string;
	containerClassName?: string;
	inputClassName?: string;
	labelClassName?: string;
	initialDanhSachMau?: any[];
	inputValue?: string;
};

const SelectTableButton = ({
	maKhoMau,
	maLoaiMau,
	setMauSelected,
	containerClassName,
	inputClassName,
	labelClassName,
	label,
	initialDanhSachMau,
	inputValue = '',
	...props
}: Props) => {

	const [tableData, setTableData] = useState<any[]>([]);
	const [isShow, setIsShow] = useState<boolean>(false);
	const [query, setQuery] = useState<string>('');
	const [selectedIndex, setSelectedIndex] = useState<number>(-1);
	const [isLoading, setIsLoading] = useState(false);
	const [pageIndex, setPageIndex] = useState(1);
	const [stopLoad, setStopLoad] = useState(false);

	const queryDebounce = useDebounce(query, 600);
	const rowSelected = tableData[selectedIndex];
	const PAGE_INDEX_DEFAULT = 1;
	const PAGE_SIZE_DEFAULT = 20;

	const pageIndexRef = useRef(PAGE_INDEX_DEFAULT);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const resetScrollFn = useRef<any>();

	useEffect(() => {
		window.addEventListener('click', checkMouseClickOutSide, true);

		return () => {
			window.removeEventListener('click', checkMouseClickOutSide, true);
		};
	}, []);

	useEffect(() => {
		if (!maKhoMau) {
			tableData.length && setTableData([]);
			return;
		}

		getDanhSachMau(queryDebounce, maKhoMau, 1);
	}, [maKhoMau, maLoaiMau, queryDebounce]);

	useEffect(() => {
		if (maKhoMau && pageIndex !== PAGE_INDEX_DEFAULT) {
			getDanhSachMau(queryDebounce, maKhoMau, pageIndex);
		}
	}, [maKhoMau, maLoaiMau, pageIndex]);

	useEffect(() => {
		setQuery(inputValue);
	}, [inputValue]);

	const checkMouseClickOutSide = (e: MouseEvent) => {
		if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
			hidePopup();
		}
	};

	const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		setQuery(inputValue);
		if (pageIndexRef.current !== PAGE_INDEX_DEFAULT) {
			pageIndexRef.current = PAGE_INDEX_DEFAULT;
			setPageIndex(PAGE_INDEX_DEFAULT);
		}
		stopLoad && setStopLoad(false);
		resetScrollFn.current?.();
	};

	const getDanhSachMau = async (keySearch = '', warehouseId: string, pageIndex: number) => {
		try {
			setIsLoading(true);
			const isFirstPage = pageIndex === PAGE_INDEX_DEFAULT;
			const response = await searchVatTu({
				warehouseId,
				materialGroup: LOAI_VAT_TU.MAU,
				materialType: maLoaiMau || undefined,
				pageIndex,
				pageSize: PAGE_SIZE_DEFAULT,
				keyword: keySearch,
			});
			const data = isFirstPage ? response : [...tableData, ...response];
			const listTemp = data.map((record: any) => ({
				...record,
				requestedPrice: record.servicePrice,
				hospitalFeePrice: record.hospitalPrice,
				BHYTPrice: record.bhytPrice,
				iventory: record.iventory - record.peShipment,
				actIngName: getIngredientValue(record.actIng),
			}));
			setTableData(listTemp);
			
			if (PAGE_SIZE_DEFAULT > response.length) {
				setStopLoad(true);
				pageIndexRef.current = PAGE_INDEX_DEFAULT;
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const getIngredientValue = (value: string | string[]) => {
		return Array.isArray(value) ? value.join(';') : value;
	};

	const onRowSelect = (rowData: any) => {
		if (!rowData) return;

		setMauSelected({
			...rowData,
			roa: rowData.ROAName,
		});
		setQuery(rowData.name);
		setSelectedIndex(0);
		setIsShow(false);
	};

	const onContainerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		const dataLength = tableData.length;

		switch (e.key) {
			case 'ArrowDown':
				if (!isShow) {
					setIsShow(true);
					return;
				}
				setSelectedIndex(selectedIndex === dataLength - 1 ? 0 : selectedIndex + 1);
				break;
			case 'ArrowUp':
				setSelectedIndex(selectedIndex === 0 ? dataLength - 1 : selectedIndex - 1);
				break;
			case 'Enter':
				e.preventDefault();
				onRowSelect(rowSelected);
				break;
			case 'Escape':
			case 'Tab':
				hidePopup();
				break;
			case 'Home':
				setSelectedIndex(0);
				break;
			case 'End':
				setSelectedIndex(dataLength - 1);
				break;

			default:
				break;
		}
	};

	const onLoadMore = () => {
		pageIndexRef.current += 1;
		setPageIndex(pageIndexRef.current);
	};

	const renderTable = () => {
		return (
			<Popover
				style={{
					minWidth: inputRef.current?.offsetWidth,
				}}
				className="popover-arrow-hidden"
			>
				<Popover.Body className="p-8 spaces">
					<TableSelect
						data={tableData}
						columns={COLUMNS}
						containerClassName="mh-350px"
						selectedRowIndex={selectedIndex}
						onSelect={(row) => {
							onRowSelect(row);
						}}
						isLoading={isLoading}
						onLoadMore={onLoadMore}
						stopLoad={stopLoad}
						resetScrollFn={resetScrollFn}
					/>
				</Popover.Body>
			</Popover>
		);
	};

	const hidePopup = () => {
		setIsShow(false);
	};

	return (
		<div className={containerClassName} ref={containerRef} onKeyDown={onContainerKeyDown}>
			<div className="d-flex align-items-center">
				<Form.Label
					className={clsx(
						'spaces text-lable-input max-content-width mb-0',
						labelClassName
					)}
				>
					{label}
				</Form.Label>
				<Form.Control
					className={clsx('spaces px-4 customs-input', inputClassName)}
					ref={inputRef}
					value={query || ""}
					onChange={onSearch}
					onFocus={() => {
						setIsShow(true);
					}}
					disabled={!maKhoMau}
					placeholder='Tìm kiếm theo mã máu, tên máu'
					{...props}
				/>
			</div>

			<Overlay
				placement="bottom-start"
				target={inputRef.current}
				container={containerRef.current}
				offset={[0, 3]}
				show={isShow}
			>
				{renderTable()}
			</Overlay>
		</div>
	);
};

export default SelectTableButton;
